import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import * as ExcelJS from 'exceljs';
import { Anchor, Borders, Cell, CellValue, Column, Style } from 'exceljs';
import { saveAs } from 'file-saver';
import { Entity } from '../classes/entity.class';
import { Planning } from '../classes/planning.class';
import { GroupedWorkorder } from '../pages/vkm/vkm-workorders/vkm-workorders.component';
import { VkmWoData } from './vkm.service';

@Injectable({ providedIn: 'root' })
export class VkmExcelExportService {
    async createExcelExport(
        data: VkmWoData,
        entitiesMap: Map<number, Entity>,
        planning: Planning | Planning[] | GroupedWorkorder[]
    ) {
        // Convert single planning to array for consistent processing
        const plannings = Array.isArray(planning) ? planning : [planning];

        // Aggregate planning data
        const projectNumbers = plannings
            .map((p: Planning | GroupedWorkorder) => p.afas_project_id)
            .filter(id => id)
            .join(', ');
        const locations = plannings
            .map((p: Planning | GroupedWorkorder) => p.location)
            .filter(loc => loc)
            .join(', ');
        const contractors = plannings
            .map((p: Planning | GroupedWorkorder) => p.contractor)
            .filter(contractor => contractor)
            .join(', ');

        // Use first planning's project ID for the title, or combined if multiple
        const titleProjectId = plannings.length === 1 ? plannings[0].afas_project_id : projectNumbers;

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Overzicht', {
            pageSetup: {
                paperSize: 9,
                orientation: 'portrait',
                scale: 66,
                margins: {
                    left: 0.3,
                    right: 0.3,
                    top: 0.3,
                    bottom: 0.3,
                    header: 0.3,
                    footer: 0.3,
                },
            },
        });

        const headerStyle = {
            font: { bold: true, color: { argb: 'FFFFFFFF' } },
            alignment: { horizontal: 'center', vertical: 'middle' },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF000080' } },
        } as Style;
        const errorStyle = {
            font: { bold: true, color: { argb: 'FFFFFFFF' } },
            alignment: { horizontal: 'center' },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF0000' } },
        } as Style;
        const centerStyle = {
            alignment: { horizontal: 'center' },
        } as Style;
        const rightStyle = {
            alignment: { indent: 1, horizontal: 'right' },
        } as Style;
        const leftStyle = {
            alignment: { indent: 1, horizontal: 'left' },
        } as Style;
        const bordersStyle = {
            top: { style: 'thin', color: { argb: 'FF000080' } },
            left: { style: 'thin', color: { argb: 'FF000080' } },
            bottom: { style: 'thin', color: { argb: 'FF000080' } },
            right: { style: 'thin', color: { argb: 'FF000080' } },
        } as Borders;

        const firstCellBoldUnderline = (cell: Cell, index: number) => {
            if (index === 1) {
                Object.assign(cell, {
                    font: { bold: true, underline: true },
                });
            }
        };

        const firstCellBold = (cell: Cell, index: number) => {
            if (index === 1) {
                Object.assign(cell, {
                    font: { bold: true },
                });
            }
        };

        worksheet.addRow([`Afrekenstaat vkm ${titleProjectId}`]).eachCell(firstCellBoldUnderline);
        worksheet.addRow([]).eachCell(firstCellBold);
        worksheet.addRow(['Projectnummer', projectNumbers]).eachCell(firstCellBold);
        worksheet.addRow(['Locatie', locations]).eachCell(firstCellBold);
        worksheet.addRow(['Tekeningnummer', data.drawingNrList.join(', ')]).eachCell(firstCellBold);
        worksheet.addRow(['Opdrachtgever', contractors]).eachCell(firstCellBold);
        worksheet.addRow([]);
        const myBase64Image =
            'data:image/svg+xml;base64,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';
        const imageId2 = workbook.addImage({
            base64: myBase64Image,
            // @ts-ignore
            extension: 'svg',
        });
        worksheet.addImage(imageId2, {
            tl: { col: 7.9, row: 0.1 } as Anchor,
            br: { col: 11, row: 5 } as Anchor,
        });

        // "Materieel" section
        const materialHeaderRow = worksheet.addRow([
            'Materieel',
            'Aanvang',
            'Einde',
            'Dagen',
            'Geleverd',
            'Huurdagen',
            'Tarief',
            'Kosten',
            'Kwijt',
            'Tarief',
            'Kosten',
        ]);
        materialHeaderRow.height = 25;
        materialHeaderRow.eachCell(cell => {
            Object.assign(cell, headerStyle);
            cell.border = bordersStyle;
        });

        const startMaterialRow = worksheet.lastRow.number + 1;
        data.objects.forEach((item: any) => {
            const row = worksheet.addRow([
                entitiesMap.get(item.entity_id).name, // Materieel placeholder (you may replace with descriptive name)
                item.date_placed ? formatDate(item.date_placed, 'dd-MM-yyyy', 'nl') : '',
                item.date_removed ? formatDate(item.date_removed, 'dd-MM-yyyy', 'nl') : '',
                item.days, // Dagen placeholder
                item.count_placed ? +item.count_placed : null,
                item.total_days, // Huurdagen placeholder
                item.price_unit ? +item.price_unit : null,
                item.price_unit_total, //formatCurrency(item.price_unit_total, 'nl', '€').replace(' ', ''),
                item.count_lost ? +item.count_lost : null,
                item.price ? +item.price : null,
                item.price_total ? item.price_total : null,
            ]);
            [7, 8, 10, 11].forEach(i => {
                row.getCell(i).style = { ...rightStyle, ...bordersStyle } as unknown as Style;
                row.getCell(i).numFmt = '"€ "#,##0.00;[Red]\-"€ "#,##0.00';
            });

            [1, 2, 3, 4, 5, 6, 9].forEach(i => {
                row.getCell(i).style = centerStyle;
            });

            row.getCell(8).value = {
                formula: `F${row.number}*G${row.number}`,
                result: item.price_unit_total,
            } as CellValue;
            row.getCell(11).value = { formula: `I${row.number}*J${row.number}`, result: item.price_total } as CellValue;

            if (item.count_placed != item.count_removed + item.count_lost) {
                row.getCell(9).style = errorStyle;
                row.getCell(9).note =
                    `Het aantal opgehaald (${item.count_removed}) + kwijt (${item.count_lost}) is niet gelijk aan het aantal items geplaatst (${item.count_placed})`;
            }
            row.eachCell(cell => {
                cell.border = bordersStyle;
                cell.font = { ...cell.font, color: { argb: 'FF000080' } };
            });
        });
        const endMaterialRow = worksheet.lastRow.number;

        worksheet.addRow([]); // Empty row for spacing

        const positionTotalPriceTable = worksheet.lastRow.number + 1;

        // "Voorbereiding en uitvoering" section
        const preparationHeaderRow = worksheet.addRow(['Voorbereiding en uitvoering', 'Aantal', 'Tarief', 'Kosten']);
        preparationHeaderRow.eachCell(cell => {
            Object.assign(cell, headerStyle);
            cell.border = bordersStyle;
        });
        preparationHeaderRow.height = 25;

        const settleGroupCellMap = new Map<string, string[]>();

        data.materials.forEach((item: any) => {
            const row = worksheet.addRow([entitiesMap.get(item.entity_id)?.name, item.count_placed, item.price_unit]);
            [1, 2, 3, 4].forEach(cellIndex => {
                if (cellIndex > 2) {
                    row.getCell(cellIndex).style = rightStyle;
                }
                row.getCell(cellIndex).border = bordersStyle;
            });
            row.getCell(3).numFmt = '"€"#,##0.00;[Red]\-"€"#,##0.00';
            row.getCell(4).numFmt = '"€"#,##0.00;[Red]\-"€"#,##0.00';
            row.getCell(2).style = centerStyle;
            row.getCell(4).value = {
                formula: `C${row.number}*B${row.number}`,
                result: item.price_unit_total,
            } as CellValue;
            row.getCell(1).style = centerStyle;
            const cells = settleGroupCellMap.get(item.settle_group) ?? [];
            cells.push(row.getCell(4).address);
            settleGroupCellMap.set(item.settle_group, cells);
            row.eachCell(cell => {
                cell.font = { ...cell.font, color: { argb: 'FF000080' } };
            });
        });
        if (data.materials?.length < data.totalPrices?.length) {
            for (let i = data.materials?.length; i < data.totalPrices?.length; i++) {
                worksheet.addRow([]);
            }
        }

        worksheet.addRow([]); // Empty row for spacing

        // "Afrekenstaat" section
        const row = worksheet.getRow(positionTotalPriceTable);
        const nameCol = 6;
        const priceCol = 10;
        row.getCell(nameCol).value = 'Afrekenstaat';
        row.getCell(nameCol).style = { ...headerStyle, ...leftStyle };
        row.getCell(nameCol).style.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
        row.getCell(nameCol).border = bordersStyle;
        worksheet.mergeCells(positionTotalPriceTable, nameCol, positionTotalPriceTable, nameCol + 3);
        row.getCell(priceCol).value = 'Kosten';
        row.getCell(priceCol).style = headerStyle;
        row.getCell(priceCol).border = bordersStyle;
        row.getCell(priceCol).style.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
        worksheet.mergeCells(positionTotalPriceTable, priceCol, positionTotalPriceTable, priceCol + 1);

        const settleGroupColumnMap = new Map([
            ['Huur materieel', 'H'],
            ['Schade', 'K'],
        ]);

        data.totalPrices.forEach((item: any, i) => {
            const rowNumber = positionTotalPriceTable + 1 + i;
            worksheet.mergeCells(rowNumber, nameCol, rowNumber, nameCol + 3);
            worksheet.mergeCells(rowNumber, priceCol, rowNumber, priceCol + 1);
            const row = worksheet.getRow(rowNumber);
            row.getCell(nameCol).value = item.settle_group;
            row.getCell(6).style = leftStyle;
            row.getCell(10).style = rightStyle;
            [6, 7, 8, 9, 10, 11].forEach(cellIndex => {
                row.getCell(cellIndex).border = bordersStyle;
            });
            if (settleGroupColumnMap.has(item.settle_group)) {
                row.getCell(priceCol).value = {
                    formula: `SUM(${settleGroupColumnMap.get(item.settle_group)}${startMaterialRow}:${settleGroupColumnMap.get(item.settle_group)}${endMaterialRow})`,
                    result: item.price,
                } as CellValue;
            } else if (settleGroupCellMap.has(item.settle_group)) {
                row.getCell(priceCol).value = {
                    formula: `${settleGroupCellMap.get(item.settle_group).join('+')}`,
                    result: item.price,
                } as CellValue;
            } else {
                row.getCell(priceCol).value = +item.price;
            }
            row.getCell(priceCol).numFmt = '"€"#,##0.00;[Red]\-"€"#,##0.00';
            row.eachCell(cell => {
                cell.font = { ...cell.font, color: { argb: 'FF000080' } };
            });
        });
        const totalStyle = {
            font: { bold: true, color: { argb: 'FF000080' } },
        };
        const totalRow = worksheet.getRow(positionTotalPriceTable + data.totalPrices.length + 1);
        totalRow.eachCell(cell => {
            cell.font = { ...cell.font, color: { argb: 'FF000080' } };
        });
        totalRow.getCell(6).style = { ...leftStyle };
        totalRow.getCell(10).style = { ...rightStyle };
        [6, 7, 8, 9, 10, 11].forEach(cellIndex => {
            totalRow.getCell(cellIndex).border = { ...bordersStyle };
        });
        totalRow.getCell(nameCol).value = 'Totaal';
        totalRow.getCell(nameCol).font = { bold: true, color: { argb: 'FF000080' } };
        totalRow.getCell(priceCol).value = {
            formula: `SUM(J${positionTotalPriceTable + 1}:J${positionTotalPriceTable + data.totalPrices.length})`,
            result: +data.totalPrice,
            // @ts-ignore
            font: { bold: true, color: { argb: 'FF000080' } },
        };
        totalRow.getCell(priceCol).font = { bold: true, color: { argb: 'FF000080' } };
        totalRow.getCell(priceCol).numFmt = '"€"#,##0.00;[Red]\-"€"#,##0.00';
        Object.assign(totalRow.getCell(priceCol), totalStyle);
        worksheet.mergeCells(totalRow.number, nameCol, totalRow.number, nameCol + 3);
        worksheet.mergeCells(totalRow.number, priceCol, totalRow.number, priceCol + 1);

        // Column widths for better visibility
        worksheet.columns = [
            { width: 30 },
            { width: 13 },
            { width: 13 },
            { width: 12 },
            { width: 10 },
            { width: 13 },
            { width: 10 },
            { width: 15 },
            { width: 10 },
            { width: 10 },
            { width: 10 },
        ] as Column[];

        worksheet.eachRow(row => {
            row.eachCell(cell => {
                cell.font = { ...cell.font, name: 'Arial' };
            });
        });

        // Save file
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, 'Overzicht.xlsx');
    }
}
